import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon, ArrowPathIcon, DocumentTextIcon, CheckCircleIcon,
  ClockIcon, ExclamationTriangleIcon, PlusIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import EmptyState from '../components/EmptyState';
import ConfirmCommandDialog from '../components/ConfirmCommandDialog';
import { fetchRfqDetail, fetchQuotationsForRfq, publishRfq, acceptQuotation } from '../api/marketplaceApi';

const RfqDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [quotations, setQuotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [acceptingId, setAcceptingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [rfqRes, quotesRes] = await Promise.allSettled([
        fetchRfqDetail(parseInt(id)),
        fetchQuotationsForRfq(parseInt(id)),
      ]);
      if (rfqRes.status === 'fulfilled') {
        setRfq(rfqRes.value.rfq || rfqRes.value);
        setItems(rfqRes.value.items || rfqRes.value.rfq?.items || []);
      }
      if (quotesRes.status === 'fulfilled') {
        setQuotations(quotesRes.value.quotations || quotesRes.value.items || []);
      }
    } catch (err) {
      console.error('Failed to load RFQ:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async () => {
    if (!id) return;
    setPublishing(true);
    try {
      await publishRfq(parseInt(id));
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleAccept = async (quotationId: number) => {
    setAcceptingId(quotationId);
    try {
      await acceptQuotation(quotationId);
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to accept quotation');
    } finally {
      setAcceptingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!rfq) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">RFQ not found</p>
        <button onClick={() => navigate('/marketplace/buyer/rfqs')} className="mt-3 text-sm text-emerald-600 hover:underline">
          Back to RFQs
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/marketplace/buyer/rfqs')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="w-4 h-4" /> Back to RFQs
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">{rfq.title || rfq.rfq_number}</h2>
            <StatusBadge status={rfq.status} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {rfq.rfq_number} · {rfq.visibility?.replace(/_/g, ' ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rfq.status === 'draft' && (
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {publishing ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
              {publishing ? 'Publishing...' : 'Publish RFQ'}
            </button>
          )}
          {rfq.status === 'draft' && (
            <button
              onClick={() => navigate(`/marketplace/buyer/rfqs/new?edit=${id}`)}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {rfq.description && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{rfq.description}</p>
            </div>
          )}

          {/* Requested Items */}
          <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Requested Items ({items.length})</h3>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">No items specified</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium text-gray-600">Product</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Quantity</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Grade/Spec</th>
                    <th className="px-4 py-2 font-medium text-gray-600 text-right">Max Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {items.map((item: any) => (
                    <tr key={item.id}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-gray-900">{item.title}</p>
                        {item.specifications && (
                          <p className="text-xs text-gray-400 mt-0.5">{item.specifications}</p>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {item.commercial_quantity} {item.commercial_unit}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{item.grade || '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {item.max_price ? <Money amount={item.max_price} currency={rfq.currency} /> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Quotations */}
          <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Supplier Quotations ({quotations.length})</h3>
            </div>
            {quotations.length === 0 ? (
              <EmptyState
                title="No quotations yet"
                description="Quotations will appear here when suppliers respond to this RFQ."
              />
            ) : (
              <div className="divide-y divide-gray-50">
                {quotations.map((q: any) => (
                  <div key={q.id} className="px-4 py-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{q.supplier_name || `Supplier #${q.supplier_tenant_id}`}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{q.quotation_number}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <StatusBadge status={q.status} size="sm" />
                          {q.delivery_lead_days && (
                            <span className="text-xs text-gray-400">Lead time: {q.delivery_lead_days} days</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          <Money amount={q.total_amount} currency={q.currency} />
                        </p>
                        {q.status === 'submitted' && (
                          <button
                            onClick={() => handleAccept(q.id)}
                            disabled={acceptingId === q.id}
                            className="mt-2 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {acceptingId === q.id ? 'Accepting...' : 'Accept Quotation'}
                          </button>
                        )}
                      </div>
                    </div>
                    {q.items && q.items.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-400">
                              <th className="text-left py-1">Item</th>
                              <th className="text-left py-1">Qty</th>
                              <th className="text-left py-1">Unit Price</th>
                              <th className="text-right py-1">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {q.items.map((qi: any) => (
                              <tr key={qi.id}>
                                <td className="py-1 text-gray-700">{qi.title}</td>
                                <td className="py-1 text-gray-500">{qi.commercial_quantity} {qi.commercial_unit}</td>
                                <td className="py-1 text-gray-500"><Money amount={qi.unit_price} currency={qi.currency} /></td>
                                <td className="py-1 text-right font-medium"><Money amount={qi.total_price} currency={qi.currency} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">RFQ Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <StatusBadge status={rfq.status} size="sm" />
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Currency</span>
                <span>{rfq.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Deadline</span>
                <span>{rfq.quote_deadline ? new Date(rfq.quote_deadline).toLocaleDateString() : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Delivery</span>
                <span className="text-right max-w-[180px] truncate">{rfq.delivery_destination || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Split Delivery</span>
                <span>{rfq.split_delivery_allowed ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payment Terms</span>
                <span className="text-right max-w-[180px] truncate">{rfq.payment_terms || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span>{new Date(rfq.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Activity */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Activity</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mt-1.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-600">RFQ created</p>
                  <p className="text-xs text-gray-400">{new Date(rfq.created_at).toLocaleString()}</p>
                </div>
              </div>
              {rfq.published_at && (
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-600">RFQ published</p>
                    <p className="text-xs text-gray-400">{new Date(rfq.published_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
              {quotations.map((q: any) => (
                <div key={q.id} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-600">Quotation received from {q.supplier_name || 'supplier'}</p>
                    <p className="text-xs text-gray-400">{new Date(q.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RfqDetail;
